import type { Action } from '../contract';
import { Chip } from '../primitives/Chip';
import { Button } from '../primitives/Button';
import { cn } from '../lib/cn';

/**
 * The kanban task card — the best idea in the prototype. The owner badge makes
 * the done-for-you tier visible on the exact screen where the owner feels the
 * work. `owner` is required (§2). Where a draft exists, the button offers it.
 */
export function TaskCard({ action, onOpen }: { action: Action; onOpen?: () => void }) {
  const done = action.status === 'done';
  const oura = action.owner === 'oura';

  const buttonLabel = action.draftReady
    ? action.draftCount && action.draftCount > 1
      ? `Read ${action.draftCount} drafts`
      : 'Read draft'
    : oura ? 'See the fix' : 'How to do it';

  return (
    <div
      onClick={done ? undefined : onOpen}
      className={cn(
        'mb-2.5 rounded-lg border border-gray-200 bg-white p-3.5 shadow-sm transition-shadow',
        done ? 'opacity-70' : 'cursor-pointer hover:shadow-md',
      )}
    >
      <p className={cn('text-sm font-semibold text-gray-900', done && 'text-gray-400 line-through')}>
        {action.title}
      </p>
      <p className="mt-1 text-sm text-gray-500">{action.body}</p>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {/* Owner badge — required, the whole point of the card */}
        <span
          className={cn(
            'inline-flex items-center gap-1.5 text-xs font-medium',
            oura ? 'text-blue-700' : 'text-gray-600',
          )}
        >
          <span
            className={cn(
              'flex h-[18px] w-[18px] items-center justify-center rounded-full text-[9px] font-bold text-white',
              oura ? 'bg-blue-600' : 'bg-gray-500',
            )}
          >
            {oura ? 'B' : 'Y'}
          </span>
          {oura ? 'Oura does this' : 'You do this'}
        </span>

        {action.estLift !== null && (
          <Chip className="border-blue-200 bg-blue-50 text-blue-700">
            {done ? '' : '+'}
            {action.estLift} pts
          </Chip>
        )}
        <span className="text-xs text-gray-400">{action.effort}</span>

        {buttonLabel && !done && (
          <Button
            size="sm"
            variant="secondary"
            className="ml-auto"
            onClick={(e) => { e.stopPropagation(); onOpen?.(); }}
          >
            {buttonLabel}
          </Button>
        )}
      </div>
    </div>
  );
}
